/**
 * Recovers a tab whose lazy chunks no longer exist on the server.
 *
 * Every route in this app is a `React.lazy()` import, and Vite emits each one as a
 * content-hashed file. A pod only ever holds ONE build — the Helm init-container extracts
 * `/usr/share/nginx/html/` from the image — and `nginx.conf` serves hashed assets `immutable`
 * while `index.html` is `no-cache`. So a tab left open across a deploy keeps the old module
 * graph in memory, asks for `ProductLineEditPage-<oldhash>.js`, and gets a 404:
 *
 *     Uncaught TypeError: Failed to fetch dynamically imported module: …/assets/ProductLineEditPage-DTFYECNL.js
 *
 * Staging makes this routine rather than rare: the Argo Rollout runs a canary, so two pods with
 * two different builds sit behind one Service and a tab that fetched `index.html` from one can
 * ask the other for a chunk it has never had.
 *
 * Nothing here retries the import — the file is genuinely gone, and the only copy of the build
 * that references it is the one in this tab. A reload is the fix: it re-fetches `index.html`
 * (which is `no-cache`, so it comes back fresh) and with it the current chunk names.
 */

/** sessionStorage key holding when this tab last reloaded itself for a missing chunk. */
const LAST_RELOAD_KEY = "wicloud:stale-chunk-reload";

/**
 * How long a reload suppresses the next one. Long enough that a chunk still missing after the
 * reload — a genuinely broken deploy, not a stale tab — surfaces the error instead of spinning
 * the tab in a loop; short enough that the same tab open for hours across a second deploy
 * recovers by itself rather than staying broken until someone reloads by hand.
 */
const RELOAD_SUPPRESSION_MS = 30_000;

/** Guards against several chunk failures in one page instance each asking for a reload. */
let reloadRequested = false;

/**
 * Whether a rejection reason is a module that could not be fetched.
 *
 * Matched on the message because there is no error type to test: the browser throws a plain
 * `TypeError`, and each engine words it differently ("Failed to fetch dynamically imported
 * module", "error loading dynamically imported module", "Importing a module script failed").
 */
function isStaleChunkError(reason: unknown): boolean {
  const message =
    reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";

  return /dynamically imported module|importing a module script failed|error loading dynamically imported/i.test(
    message,
  );
}

/**
 * Reads the last reload stamp, or null when this tab cannot remember one.
 *
 * Null is NOT the same as "never reloaded": a tab with storage blocked (private window, a
 * policy) can never remember that it already tried, so the caller must not reload at all
 * rather than risk a tab that reloads forever.
 */
function readLastReload(): number | null {
  try {
    return Number(window.sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
  } catch {
    return null;
  }
}

/**
 * Reloads the tab, at most once per {@link RELOAD_SUPPRESSION_MS}.
 *
 * Returns whether a reload was actually started, because the caller must only swallow the
 * failure when one was: suppressing an error nobody is going to act on turns a stale chunk into
 * a blank screen with nothing in the console to explain it.
 */
function reloadForStaleChunk(): boolean {
  if (reloadRequested) {
    // A reload is already on its way; a second failure in the same page is the same failure.
    return true;
  }

  const lastReload = readLastReload();
  if (lastReload === null || Date.now() - lastReload < RELOAD_SUPPRESSION_MS) {
    // Either we cannot remember having tried, or we tried moments ago and the chunk is still
    // missing. Let the error reach the console: a reload loop is worse than a visible failure.
    return false;
  }

  try {
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }

  reloadRequested = true;
  window.location.reload();

  return true;
}

/**
 * Starts listening for stale-chunk failures. Call once, before the app renders.
 */
export function installStaleChunkRecovery(): void {
  // Vite's own signal, raised by the `__vitePreload` helper when a chunk or its preload fails.
  //
  // preventDefault ONLY when a reload is actually starting. Vite rethrows the error unless the
  // event is cancelled, so cancelling it makes the failed `import()` resolve with `undefined` —
  // and `React.lazy` then dies reading `.default` of that, which is a worse error than the real
  // one and points nowhere near the real cause. While a reload is in flight nobody reads either
  // message, so swallowing it is free; once reloading is suppressed, the honest failure is the
  // more useful outcome.
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadForStaleChunk()) {
      event.preventDefault();
    }
  });

  // The import itself can still reject without a preload error — a chunk with no preload link,
  // or a `React.lazy` retry — and that arrives as an unhandled rejection.
  window.addEventListener("unhandledrejection", (event) => {
    if (!isStaleChunkError(event.reason)) {
      return;
    }

    if (reloadForStaleChunk()) {
      event.preventDefault();
    }
  });
}

/** Exported for tests only. */
export const __testing = { isStaleChunkError, LAST_RELOAD_KEY, RELOAD_SUPPRESSION_MS };
